/**
 * Compute macro totals from menu builders (selected items: quantity > 0).
 * Matches logic used in useMenuState / menu store.
 * Per-day macros from subscription **`particularsDaily`** (see `buildDayMacroMapFromSubscriptionsParticulars`).
 */
import moment from 'moment';
import type { SubscriptionData } from '../subscription/types';
import type { MenuBuilder } from './types';
import { isDateFrozen, getSubscriptionPausedDateSet, getSubscriptionSkippedDateSet } from '../subscription';

export interface MacroTotals {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

function addItemMacros(
  it: { quantity?: number; calories?: string | number; protein?: string | number; carbs?: string | number; fats?: string | number },
  acc: { calories_cents: number; protein_cents: number; carbs_cents: number; fats_cents: number },
): void {
  const q = it.quantity ?? 0;
  if (q <= 0) {return;}
  acc.calories_cents += Math.round(100 * parseFloat(String(it.calories ?? 0)) * q);
  acc.protein_cents += Math.round(100 * parseFloat(String(it.protein ?? 0)) * q);
  acc.carbs_cents += Math.round(100 * parseFloat(String(it.carbs ?? 0)) * q);
  acc.fats_cents += Math.round(100 * parseFloat(String(it.fats ?? 0)) * q);
}

export function computeMacrosFromBuilders(builders: MenuBuilder[] | undefined): MacroTotals | null {
  if (!builders?.length) {return null;}
  const acc = { calories_cents: 0, protein_cents: 0, carbs_cents: 0, fats_cents: 0 };
  for (const b of builders) {
    for (const it of b.type_data ?? []) {
      addItemMacros(it, acc);
      for (const r of (it as { replaceable_menu_items?: Array<{ quantity?: number; calories?: string | number; protein?: string | number; carbs?: string | number; fats?: string | number }> }).replaceable_menu_items ?? []) {
        addItemMacros(r, acc);
      }
    }
  }
  const calories = Math.round(acc.calories_cents / 100);
  const protein = Math.round(acc.protein_cents / 100);
  const carbs = Math.round(acc.carbs_cents / 100);
  const fats = Math.round(acc.fats_cents / 100);
  if (calories === 0 && protein === 0 && carbs === 0 && fats === 0) {return null;}
  return { calories, protein, carbs, fats };
}

function normalizeParticularDateKey(raw: unknown): string | null {
  if (raw == null) {return null;}
  const s = String(raw).trim();
  if (!s) {return null;}
  let m = moment(s, ['YYYY-MM-DD', moment.ISO_8601, 'DD/MM/YYYY', 'MM/DD/YYYY'], true);
  if (!m.isValid()) {
    m = moment(s);
  }
  if (!m.isValid()) {return null;}
  return m.format('YYYY-MM-DD');
}

/** Resolve calendar day from a particulars row (wire shapes vary). */
function dateKeyFromParticularRow(row: Record<string, unknown>): string | null {
  const candidates = [row.date, row.delivery_date, row.day, row.scheduled_date, row.menu_date];
  for (const c of candidates) {
    const k = normalizeParticularDateKey(c);
    if (k) {return k;}
  }
  return null;
}

/** Macros from one particulars row: nested `macronutrient(s)` or flat numbers on the row. */
function macrosFromParticularRow(row: Record<string, unknown>): MacroTotals | null {
  const nested = row.macronutrient ?? row.macronutrients ?? row.macros;
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const o = nested as Record<string, unknown>;
    const calories = Math.round(Number(o.calories ?? o.calorie ?? 0)) || 0;
    const protein = Math.round(Number(o.protein ?? 0)) || 0;
    const carbs = Math.round(Number(o.carbs ?? o.carb ?? 0)) || 0;
    const fats = Math.round(Number(o.fats ?? o.fat ?? 0)) || 0;
    if (calories === 0 && protein === 0 && carbs === 0 && fats === 0) {return null;}
    return { calories, protein, carbs, fats };
  }
  const calories = Math.round(Number(row.calories ?? row.calorie ?? 0)) || 0;
  const protein = Math.round(Number(row.protein ?? 0)) || 0;
  const carbs = Math.round(Number(row.carbs ?? row.carb ?? 0)) || 0;
  const fats = Math.round(Number(row.fats ?? row.fat ?? 0)) || 0;
  if (calories === 0 && protein === 0 && carbs === 0 && fats === 0) {return null;}
  return { calories, protein, carbs, fats };
}

/**
 * One map entry per calendar day from **`particularsDaily`** on **`SubscriptionData`** (already merged from the three
 * subscription fetches in `fetchSubscriptionData` / `api.ts`). Later entries overwrite earlier (same date from
 * multiple subs or duplicate rows).
 * Used by: `buildDayMacroMapForDashboardWeek`.
 */
export function buildDayMacroMapFromSubscriptionsParticulars(subscriptions: SubscriptionData[]): Map<string, MacroTotals> {
  const map = new Map<string, MacroTotals>();
  for (const sub of subscriptions) {
    const items = sub.particularsDaily;
    if (!Array.isArray(items)) {continue;}
    for (const row of items) {
      if (row == null || typeof row !== 'object') {continue;}
      const rec = row as Record<string, unknown>;
      const date_key = dateKeyFromParticularRow(rec);
      if (!date_key) {continue;}
      const macros = macrosFromParticularRow(rec);
      if (!macros) {continue;}
      map.set(date_key, macros);
    }
  }
  return map;
}

/**
 * Target macros from merged **`particularsMacros`** (aggregate object on `SubscriptionData`).
 * Used by: `buildDayMacroMapForDashboardWeek` for dates inside the subscription window when there is no per-day row.
 */
export function macroTotalsFromParticularsAggregate(macros: Record<string, unknown> | undefined): MacroTotals | null {
  if (macros == null || typeof macros !== 'object' || Array.isArray(macros)) {
    return null;
  }
  const calories = Math.round(Number(macros.calories ?? macros.calorie ?? 0)) || 0;
  const protein = Math.round(Number(macros.protein ?? 0)) || 0;
  const carbs = Math.round(Number(macros.carbs ?? macros.carb ?? 0)) || 0;
  const fats = Math.round(Number(macros.fats ?? macros.fat ?? 0)) || 0;
  if (calories === 0 && protein === 0 && carbs === 0 && fats === 0) {
    return null;
  }
  return { calories, protein, carbs, fats };
}

/**
 * Week window: per-day macros from **`particularsDaily`** first; any date in `date_list` still missing gets
 * **`particularsMacros`** for that subscription when the date is in `[start_date, end_date]` (skips frozen days).
 * Used by: `useMacrosFromApi`.
 */
export function buildDayMacroMapForDashboardWeek(subscriptions: SubscriptionData[], date_list: string[]): Map<string, MacroTotals> {
  const map = buildDayMacroMapFromSubscriptionsParticulars(subscriptions);
  for (const date of date_list) {
    if (map.has(date)) {
      continue;
    }
    for (const sub of subscriptions) {
      const d = moment(date, 'YYYY-MM-DD', true);
      const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
      if (!d.isValid() || !start.isValid() || !end.isValid()) {
        continue;
      }
      if (!d.isBetween(start, end, 'day', '[]')) {
        continue;
      }
      if (isDateFrozen(date, sub.freeze_dates ?? [])) {
        continue;
      }
      if (getSubscriptionSkippedDateSet(sub).has(date) || getSubscriptionPausedDateSet(sub).has(date)) {
        continue;
      }
      const totals = macroTotalsFromParticularsAggregate(sub.particularsMacros);
      if (totals) {
        map.set(date, totals);
        break;
      }
    }
  }
  return map;
}
